import { useCallback, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const HEARTBEAT_MS = 15 * 1000;
const INITIAL_PING_MS = 5 * 1000;
const MIN_FLUSH_SECONDS = 3;

const PUBLIC_PREFIXES = [
  '/login',
  '/survey/',
  '/verify',
  '/careers',
  '/play',
  '/land1',
  '/land2',
  '/land3',
  '/privacy-policy',
  '/terms',
  '/ads',
  '/join/',
  '/certificate/'
];

const getLocalDateKey = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const resolveUserId = (user) => String(user?.id || user?._id || '').trim();

const shouldTrackPath = (pathname) => {
  if (!pathname || pathname === '/' || pathname === '/home') return false;
  return !PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix));
};

const getPageLabel = (pathname) => {
  if (typeof document !== 'undefined' && document.title) {
    const title = document.title.replace(/\s*[|\-–—]\s*RTC.*$/i, '').trim();
    if (title) return title.slice(0, 200);
  }
  return pathname;
};

const sendPing = (payload, useKeepalive = false) => {
  const token = localStorage.getItem('token');
  if (!token) return;

  const url = `${API_URL}/user-activity/ping`;

  if (useKeepalive) {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(() => {});
    return;
  }

  axios.post(url, payload).catch(() => {});
};

export function usePageActivityTracker() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const pathRef = useRef('');
  const labelRef = useRef('');
  const startedAtRef = useRef(Date.now());
  const isNewVisitRef = useRef(true);
  const userId = resolveUserId(user);

  const flush = useCallback((useBeacon = false) => {
    if (!userId) return;
    const path = pathRef.current;
    if (!path || !shouldTrackPath(path)) return;

    const elapsedSeconds = Math.round((Date.now() - startedAtRef.current) / 1000);
    if (elapsedSeconds < MIN_FLUSH_SECONDS) return;

    sendPing({
      path,
      label: labelRef.current,
      durationSeconds: elapsedSeconds,
      date: getLocalDateKey(),
      isNewVisit: isNewVisitRef.current
    }, useBeacon);

    startedAtRef.current = Date.now();
    isNewVisitRef.current = false;
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;

    const nextPath = location.pathname;
    if (pathRef.current && pathRef.current !== nextPath) {
      flush();
    }

    pathRef.current = nextPath;
    labelRef.current = getPageLabel(nextPath);
    startedAtRef.current = Date.now();
    isNewVisitRef.current = true;

    if (!shouldTrackPath(nextPath)) {
      pathRef.current = '';
      return undefined;
    }

    const initialPing = setTimeout(() => flush(), INITIAL_PING_MS);
    const heartbeat = setInterval(() => flush(), HEARTBEAT_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush(true);
    };
    const onUnload = () => flush(true);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onUnload);

    return () => {
      clearTimeout(initialPing);
      clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onUnload);
      flush(true);
    };
  }, [userId, location.pathname, flush]);
}

export default usePageActivityTracker;
